'use client';

import { memo, useState } from 'react';

import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Trans } from '@kit/ui/trans';

import type {
  BodyShape,
  BodyType,
  CharacterConfig,
  ChestSize,
  DickSize,
  Gender,
  StyleType,
} from './types';

interface Step4BodyTypeProps {
  config: CharacterConfig;
  updateConfig: (updates: Partial<CharacterConfig>) => void;
  onBack: () => void;
  onNext: () => void;
}

const bodyTypes: BodyType[] = [
  'petite',
  'slim',
  'athletic',
  'voluptuous',
  'curvy',
];

const chestSizes: ChestSize[] = ['flat', 'small', 'medium', 'large', 'huge'];

const buttSizes: BodyShape[] = ['small', 'athletic', 'medium', 'large', 'huge'];

const dickSizes: DickSize[] = ['small', 'average', 'medium', 'large', 'huge'];

function getBodyTypeImage(id: string, style: StyleType, gender: Gender) {
  // realistic/female not in card-images, fall back to existing folder
  if (style === 'realistic' && gender === 'female') {
    return `/images/character-creator/4th-step/body-type/realistic/female/${id}.avif`;
  }
  return `/images/character-creator/card-images/4th-step/body-type/${style}/${gender}/${id}/1.avif`;
}

function getChestImage(id: string, style: StyleType, gender: Gender) {
  // realistic/female not in card-images, fall back to existing folder
  if (style === 'realistic' && gender === 'female') {
    return `/images/character-creator/4th-step/chest/realistic/female/${id}.avif`;
  }
  return `/images/character-creator/card-images/4th-step/chest/${style}/${gender}/${id}/1.avif`;
}

function getButtImage(id: string, style: StyleType, gender: Gender) {
  // realistic/female not in card-images, fall back to existing folder
  if (style === 'realistic' && gender === 'female') {
    return `/images/character-creator/4th-step/butt-size/realistic/female/${id}.avif`;
  }
  return `/images/character-creator/card-images/4th-step/butt-size/${style}/${gender}/${id}/1.avif`;
}

function getDickImage(id: string, style: StyleType, gender: Gender) {
  return `/images/character-creator/card-images/4th-step/dick-size/${style}/${gender}/${id}/1.avif`;
}

function ImageCard({
  src,
  alt,
  isSelected,
  onClick,
}: {
  src: string;
  alt: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  const [imgError, setImgError] = useState(false);

  return (
    <button
      onClick={onClick}
      style={{ aspectRatio: '7 / 10' }}
      className={`relative overflow-hidden rounded-md transition-all duration-300 sm:rounded-lg ${
        isSelected
          ? 'border-2 border-pink-500 shadow-[0_0_10px_rgba(236,72,153,0.5)]'
          : 'border border-white/10 hover:border-white/30'
      }`}
    >
      {!imgError ? (
        <img
          src={src}
          alt={alt}
          className="h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
          <span className="px-1 text-center text-[9px] font-medium text-gray-400 sm:text-[10px]">
            {alt}
          </span>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
      <div className="absolute right-0 bottom-0 left-0 p-0.5 sm:p-1">
        <h3 className="text-center text-[7px] font-bold text-white sm:text-[8px] md:text-[10px]">
          {alt}
        </h3>
      </div>
      {isSelected && (
        <div className="absolute top-0.5 right-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-pink-500 sm:top-1 sm:right-1 sm:h-3.5 sm:w-3.5">
          <Check className="h-2 w-2 text-white sm:h-2.5 sm:w-2.5" />
        </div>
      )}
    </button>
  );
}

// Show chest size for female and trans, hide for male
function shouldShowChestSize(gender: Gender): boolean {
  return gender !== 'male';
}

// Show dick size for male and trans, hide for female
function shouldShowDickSize(gender: Gender): boolean {
  return gender !== 'female';
}

export const Step4BodyType = memo(function Step4BodyType({
  config,
  updateConfig,
  onBack,
  onNext,
}: Step4BodyTypeProps) {
  const { t } = useTranslation('common');

  return (
    <div className="mx-auto max-w-5xl px-2 sm:px-4 lg:px-0">
      {/* Title */}
      <div className="mb-2 text-center sm:mb-2.5">
        <h1 className="mb-0.5 text-lg font-bold text-white sm:mb-1 sm:text-xl md:text-2xl">
          <Trans i18nKey="common:characterCreator.step1Title" />
        </h1>
        <p className="bg-gradient-to-r from-red-400 via-pink-400 to-pink-600 bg-clip-text text-[10px] font-semibold text-transparent sm:text-xs md:text-sm">
          <Trans i18nKey="common:characterCreator.step4BodyDetails" />
        </p>
      </div>

      {/* Select Body Type */}
      <div className="mb-2 sm:mb-2.5">
        <h3 className="mb-1.5 text-center text-[10px] font-semibold text-white sm:mb-2 sm:text-xs md:text-sm">
          <Trans i18nKey="common:characterCreator.secBodyType" />
        </h3>
        <div className="mx-auto grid max-w-full grid-cols-5 gap-1 sm:max-w-sm sm:gap-1.5 md:max-w-xl md:gap-2 lg:max-w-2xl">
          {bodyTypes.map((id) => (
            <ImageCard
              key={id}
              src={getBodyTypeImage(id, config.style, config.gender)}
              alt={t(`characterCreator.options.bodyType.${id}`)}
              isSelected={config.bodyType === id}
              onClick={() => updateConfig({ bodyType: id })}
            />
          ))}
        </div>
      </div>

      {/* Select Chest Size - only for female and trans */}
      {shouldShowChestSize(config.gender) && (
        <div className="mb-2 sm:mb-2.5">
          <h3 className="mb-1.5 text-center text-[10px] font-semibold text-white sm:mb-2 sm:text-xs md:text-sm">
            <Trans i18nKey="common:characterCreator.secChestSize" />
          </h3>
          <div className="mx-auto grid max-w-full grid-cols-5 gap-1 sm:max-w-sm sm:gap-1.5 md:max-w-xl md:gap-2 lg:max-w-2xl">
            {chestSizes.map((id) => (
              <ImageCard
                key={id}
                src={getChestImage(id, config.style, config.gender)}
                alt={t(`characterCreator.options.chestSize.${id}`)}
                isSelected={config.chestSize === id}
                onClick={() => updateConfig({ chestSize: id })}
              />
            ))}
          </div>
        </div>
      )}

      {/* Select Butt Size */}
      <div className="mb-2 sm:mb-2.5">
        <h3 className="mb-1.5 text-center text-[10px] font-semibold text-white sm:mb-2 sm:text-xs md:text-sm">
          <Trans i18nKey="common:characterCreator.secButtSize" />
        </h3>
        <div className="mx-auto grid max-w-full grid-cols-5 gap-1 sm:max-w-sm sm:gap-1.5 md:max-w-xl md:gap-2 lg:max-w-2xl">
          {buttSizes.map((id) => (
            <ImageCard
              key={id}
              src={getButtImage(id, config.style, config.gender)}
              alt={t(`characterCreator.options.buttSize.${id}`)}
              isSelected={config.bodyShape === id}
              onClick={() => updateConfig({ bodyShape: id })}
            />
          ))}
        </div>
      </div>

      {/* Select Dick Size - only for male and trans */}
      {shouldShowDickSize(config.gender) && (
        <div className="mb-2 sm:mb-2.5">
          <h3 className="mb-1.5 text-center text-[10px] font-semibold text-white sm:mb-2 sm:text-xs md:text-sm">
            <Trans i18nKey="common:characterCreator.secDickSize" />
          </h3>
          <div className="mx-auto grid max-w-full grid-cols-5 gap-1 sm:max-w-sm sm:gap-1.5 md:max-w-xl md:gap-2 lg:max-w-2xl">
            {dickSizes.map((id) => (
              <ImageCard
                key={id}
                src={getDickImage(id, config.style, config.gender)}
                alt={t(`characterCreator.options.dickSize.${id}`)}
                isSelected={config.dickSize === id}
                onClick={() => updateConfig({ dickSize: id })}
              />
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-2 flex justify-center gap-2 sm:mt-3 sm:gap-2.5">
        <button
          onClick={onBack}
          className="rounded-lg border border-white/30 bg-transparent px-4 py-1.5 text-[10px] font-semibold text-white transition-all hover:border-white/50 hover:bg-white/5 sm:border-2 sm:px-6 sm:py-2 sm:text-xs md:px-8"
        >
          <Trans i18nKey="common:characterCreator.backButton" />
        </button>

        <button
          onClick={onNext}
          className="rounded-lg bg-gradient-to-r from-pink-600 via-pink-500 to-pink-700 px-6 py-1.5 text-[10px] font-bold text-white shadow-lg shadow-pink-500/30 transition-all hover:from-pink-700 hover:via-pink-600 hover:to-pink-800 sm:px-8 sm:py-2 sm:text-xs md:px-10"
        >
          <Trans i18nKey="common:characterCreator.nextButton" />
        </button>
      </div>
    </div>
  );
});
